/**
 * DogVaxRecords (#752) — the dog's uploaded vaccination records, inside the
 * profile's health section.
 *
 * Ted (2026-09-25): "could we make vaccination records easy for clients to
 * upload and easy for staff to find, open, and read from the dog's
 * immunization section? If a record is uploaded but the dates still need
 * to be entered or verified, it would help to make that clear to staff."
 *
 * Two jobs, in that order:
 *   1. A record waiting for review is a CALLOUT, stated before the table —
 *      the table may be empty or stale precisely because of it, and the
 *      callout is what explains the blank dates (Ted's second screenshot).
 *   2. Every record with a stored original gets a "View" that opens it
 *      full screen. One place to look, not three.
 *
 * Only VERIFIED facts are shown as facts. A pending record's parsed dates
 * never appear here (the server does not send them); the callout links to
 * the review, where the draft and the document sit side by side.
 */
import { Link } from 'react-router-dom';
import type { DogVaxRecord, VaxDocumentMeta } from '../../lib/api.js';
import { formatDate } from './format.js';

export interface OpenDocument {
  submissionId: string;
  document: VaxDocumentMeta;
}

const SOURCE_LABEL: Record<DogVaxRecord['source'], string> = {
  customer_upload: 'Uploaded by the owner',
  email: 'Emailed in',
};

interface PendingCalloutsProps {
  dogName: string;
  records: ReadonlyArray<DogVaxRecord>;
  /** May this viewer open the review queue (`/vax-inbox`, pets:write)? */
  canReview: boolean;
  onView: (open: OpenDocument) => void;
}

/** Job 1 — rendered ABOVE the vaccination table. */
export function PendingVaxRecordCallouts({
  dogName,
  records,
  canReview,
  onView,
}: PendingCalloutsProps): JSX.Element | null {
  const pending = records.filter((r) => r.status === 'pending');
  if (pending.length === 0) return null;
  return (
    <div className="ferro-dogprofile__vax-pending-list">
      {pending.map((r) => (
        <div
          key={r.id}
          className="ferro-dogprofile__alert ferro-dogprofile__alert--warning ferro-dogprofile__vax-pending"
          role="status"
          data-testid="dogprofile-vax-pending"
        >
          <p className="ferro-dogprofile__vax-pending-text">
            <strong>Vaccination record waiting for review.</strong>{' '}
            {SOURCE_LABEL[r.source]} {formatDate(r.receivedAt) ?? ''}.{' '}
            {r.parseStatus === 'ok'
              ? `The dates below won't change until someone checks it against ${dogName}'s record.`
              : "We couldn't read the dates automatically, so someone needs to enter them from the record."}
          </p>
          <div className="ferro-dogprofile__vax-pending-actions">
            {r.documents[0] && !r.documents[0].deleted ? (
              <button
                type="button"
                className="ferro-btn ferro-btn--ghost"
                onClick={() => onView({ submissionId: r.id, document: r.documents[0]! })}
                data-testid="dogprofile-vax-pending-view"
              >
                View record
              </button>
            ) : null}
            {canReview ? (
              <Link
                className="ferro-btn ferro-btn--primary"
                to={`/vax-inbox?focus=${encodeURIComponent(r.id)}`}
                data-testid="dogprofile-vax-review-link"
              >
                Review &amp; enter dates
              </Link>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  );
}

interface FilesProps {
  records: ReadonlyArray<DogVaxRecord>;
  onView: (open: OpenDocument) => void;
}

/** Job 2 — every uploaded record, rendered BELOW the vaccination table. */
export function VaxRecordFiles({ records, onView }: FilesProps): JSX.Element | null {
  if (records.length === 0) return null;
  return (
    <div className="ferro-dogprofile__vax-records" data-testid="dogprofile-vax-records">
      <h3 className="ferro-dogprofile__subhead">Uploaded records</h3>
      <ul className="ferro-dogprofile__vax-files">
        {records.map((r) => (
          <li key={r.id} className="ferro-dogprofile__vax-file">
            <div className="ferro-dogprofile__vax-file-meta">
              <span className="ferro-dogprofile__vax-file-source">
                {SOURCE_LABEL[r.source]} · {formatDate(r.receivedAt) ?? '—'}
              </span>
              {r.status === 'approved' ? (
                <span className="ferro-dogprofile__verified">
                  Verified{r.reviewedAt ? ` ${formatDate(r.reviewedAt) ?? ''}` : ''}
                </span>
              ) : (
                <span className="ferro-dogprofile__vax-needs-review">Needs review</span>
              )}
            </div>
            {r.documents.length > 0 ? (
              r.documents.map((d, i) =>
                d.deleted ? (
                  // #1182 — deleted on rejection; nothing to open.
                  <span
                    key={d.id}
                    className="ferro-dogprofile__vax-file-deleted ferro-muted"
                    data-testid="dogprofile-vax-deleted"
                  >
                    {r.documents.length > 1 ? `Page ${i + 1} deleted on rejection` : 'Deleted on rejection'}
                  </span>
                ) : (
                  <button
                    key={d.id}
                    type="button"
                    className="ferro-btn ferro-btn--ghost ferro-dogprofile__vax-view"
                    onClick={() => onView({ submissionId: r.id, document: d })}
                    data-testid="dogprofile-vax-view"
                  >
                    {r.documents.length > 1 ? `View page ${i + 1}` : 'View'}
                  </button>
                ),
              )
            ) : (
              // Submissions from before #752 were read and discarded. Say
              // so, rather than rendering a button that opens nothing.
              <span className="ferro-dogprofile__unverified">Original not kept</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
